import { useState, useEffect, useCallback, useLayoutEffect, useRef } from 'react';
import { RgbColorPicker, RgbColor } from 'react-colorful';
import { Button, TextInput } from '@patternfly/react-core';

import './color-picker.css';

interface ColorPickerProps {
    color: [number, number, number];
    onChange: (color: [number, number, number]) => void;
    textInputId: string;
    isDisabled?: boolean;
    label?: string;
    /** Called when the picker opens or closes, so the owning row can mark itself as being edited. */
    onOpenChange?: (isOpen: boolean) => void;
}

// Constants for color preview dimensions
const COLOR_PREVIEW_WIDTH = 48;
const COLOR_PREVIEW_HEIGHT = 24;

// Vertical gap between the color preview and the picker frame.
const FRAME_GAP = 4;

// Helper to format RGB array as a string
const getTextValue = (color: [number, number, number]): string =>
    `${color[0]}, ${color[1]}, ${color[2]}`;

// Helper to validate if a string is a valid RGB color
const isTextValidColor = (value: string): boolean => {
    const rgb = value.split(',').map((v) => parseInt(v.trim(), 10));
    return rgb.length === 3 && rgb.every((val) => val >= 0 && val <= 255);
};

// Pick the theme's dark or light text token so the label stays legible on any
// swatch color. Uses Rec. 601 perceived-luminance coefficients.
const getContrastingTextColor = (color: [number, number, number]): string => {
    const luminance = (0.299 * color[0] + 0.587 * color[1] + 0.114 * color[2]) / 255;
    return luminance > 0.5 ? 'var(--near-black)' : 'var(--white)';
};


// IMPORTANT: Any changes that propagate to react-color (i.e. changing or adjusting SketchPicker) may
// require updating the style-src content security policy hashes in public/manifest.json. Chrome will
// generate these for you in an error message when you open dev tools.
//
// In other words:
// If you change this component, test that everything works correctly in a Cockpit environment!


const ColorPicker = ({
    color,
    onChange,
    textInputId,
    isDisabled = false,
    label,
    onOpenChange,
}: ColorPickerProps) => {
    const [isModalOpen, setModalOpen] = useState(false);
    const [textInputValue, setTextInputValue] = useState(getTextValue(color));
    // Vertical offset of the frame within the table cell, or null until it has been measured.
    const [frameTop, setFrameTop] = useState<number | null>(null);
    const triggerRef = useRef<HTMLDivElement>(null);
    const frameRef = useRef<HTMLDivElement>(null);

    // Sync the text input value when the external color prop changes
    useEffect(() => {
        setTextInputValue(getTextValue(color));
    }, [color]);

    // Open the color picker modal. onOpenChange lets the owning row highlight itself for as long
    // as the picker is open; it is called here rather than from an effect so that the row and the
    // picker frame update in the same commit.
    const handleOpenModal = useCallback(() => {
        if (!isDisabled) {
            setModalOpen(true);
            onOpenChange?.(true);
        }
    }, [isDisabled, onOpenChange]);

    // Close the color picker modal
    const handleCloseModal = useCallback(() => {
        setModalOpen(false);
        onOpenChange?.(false);
    }, [onOpenChange]);

    // The frame is a plain absolutely positioned element inside the table cell rather than a
    // portalled popper, so it scrolls in lockstep with its own row: no per-scroll repositioning,
    // and therefore no lag, no bouncing and no mid-scroll flipping between above and below.
    // The only positioning decision is which side of the swatch to open on, made once here.
    useLayoutEffect(() => {
        if (!isModalOpen) {
            setFrameTop(null);
            return;
        }

        const trigger = triggerRef.current;
        const frame = frameRef.current;
        if (!trigger || !frame) { return; }

        const triggerRect = trigger.getBoundingClientRect();
        const frameHeight = frame.offsetHeight;

        // The frame scrolls underneath the sticky table header (see color-picker.css), so the
        // usable space above the trigger ends at the stuck header's bottom edge, not at the
        // viewport top — a frame opened into that band would start out hidden under the header
        // with no way to scroll it back out. A frame that fits neither way still opens
        // downwards: past the viewport bottom it can at least be scrolled into view.
        const headerRect = trigger.closest('table')?.querySelector('thead th')?.getBoundingClientRect();
        const topLimit = headerRect?.bottom ?? 0;

        // Open downwards unless that would put the frame past the bottom of the viewport while
        // there is room above it. This is what keeps the frame from opening partially off-screen.
        const fitsBelow = triggerRect.bottom + FRAME_GAP + frameHeight <= window.innerHeight;
        const fitsAbove = triggerRect.top - FRAME_GAP - frameHeight >= topLimit;
        const openUpwards = !fitsBelow && fitsAbove;

        // offsetTop/offsetHeight are relative to the table cell, which PatternFly gives
        // `position: relative` — the same containing block the frame is positioned against.
        setFrameTop(openUpwards
            ? trigger.offsetTop - FRAME_GAP - frameHeight
            : trigger.offsetTop + trigger.offsetHeight + FRAME_GAP);
    }, [isModalOpen]);

    // Close the picker when interacting with anything outside it. This listens for mousedown
    // rather than click so that dragging a react-colorful handle out of the frame and releasing
    // there doesn't count as an outside interaction.
    useEffect(() => {
        if (!isModalOpen) { return; }

        const handleOutsideMouseDown = (event: MouseEvent) => {
            const target = event.target as Node | null;
            if (!target) { return; }
            if (frameRef.current?.contains(target) || triggerRef.current?.contains(target)) { return; }

            handleCloseModal();
        };

        document.addEventListener('mousedown', handleOutsideMouseDown);
        return () => { document.removeEventListener('mousedown', handleOutsideMouseDown); };
    }, [isModalOpen, handleCloseModal]);

    // Handle changes from the color picker
    const handleColorPickerChange = useCallback(
        (rgb: RgbColor) => {
            if (isDisabled) { return; }

            const colorTuple: [number, number, number] = [rgb.r, rgb.g, rgb.b];
            setTextInputValue(getTextValue(colorTuple)); // Update text input
            onChange(colorTuple); // Notify parent component
        },
        [isDisabled, onChange]
    );

    // Handle text input change
    const handleColorTextInputChange = useCallback(
        (_: unknown, value: string) => {
            if (isDisabled) { return; }

            setTextInputValue(value); // Update text input value immediately

            if (isTextValidColor(value)) {
                const rgb = value.split(',').map((v) => parseInt(v.trim(), 10)) as [
                    number,
                    number,
                    number
                ];
                onChange(rgb); // Notify parent component if valid
            }
        },
        [isDisabled, onChange]
    );

    // Handle blur event on the text input
    const handleBlur = useCallback(() => {
        if (isDisabled) { return; }

        if (!isTextValidColor(textInputValue)) {
            setTextInputValue(getTextValue(color)); // Reset invalid input to the last valid value
        }
    }, [isDisabled, color, textInputValue]);

    return (
        <div className={`color-picker ${isModalOpen ? 'is-open' : ''}`}>
            {/* Color preview box */}
            <div className="color-preview" data-testid="color-picker-preview" onClick={handleOpenModal} ref={triggerRef}>
                <svg width={COLOR_PREVIEW_WIDTH} height={COLOR_PREVIEW_HEIGHT}>
                    <rect
                        x="0"
                        y="0"
                        width={COLOR_PREVIEW_WIDTH}
                        height={COLOR_PREVIEW_HEIGHT}
                        fill={`rgb(${color[0]}, ${color[1]}, ${color[2]})`}
                    />
                </svg>
            </div>

            {isModalOpen && (
                <div
                    className="color-picker-frame"
                    ref={frameRef}
                    // Kept hidden for the single render before the layout effect has measured the
                    // frame and worked out which side of the swatch it opens on.
                    style={frameTop === null ? { visibility: 'hidden' } : { top: `${frameTop}px` }}
                >
                    {/* Text input for RGB values */}
                    <div className="rgb-text-box">
                        <TextInput
                            type="text"
                            className="color-picker-input"
                            id={textInputId}
                            value={textInputValue}
                            onChange={handleColorTextInputChange}
                            onBlur={handleBlur}
                            isDisabled={isDisabled}
                            aria-label={label || 'Color picker input'}
                            data-testid="color-picker-input"
                        />
                    </div>

                    {/* RgbColorPicker component */}
                    <RgbColorPicker
                        color={{ r: color[0], g: color[1], b: color[2] }}
                        onChange={handleColorPickerChange}
                        className="rgb-color-picker"
                    />

                    {/* Close button */}
                    <div className="close-button">
                        <Button
                            variant="primary"
                            className="close-color-picker-button"
                            data-testid="color-picker-close-button"
                            style={{
                                backgroundColor: `rgb(${color[0]}, ${color[1]}, ${color[2]})`,
                                color: getContrastingTextColor(color),
                            }}
                            onClick={handleCloseModal}
                        >
                            Close color picker
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ColorPicker;
